import React, { useCallback, useEffect, useMemo } from 'react';
import { Calendar, Clock, Building, Briefcase, Sparkles, Globe, User, Play } from 'lucide-react';
import { Button } from '../common/Button';
import Modal from '../common/Modal';
import { RecommendedItem } from '../../types/recommendations';
import { InfoTooltip } from '../common/InfoTooltip';
import { DurationDisplay } from '../common/DurationDisplay';

interface InterviewForYouModalProps {
  isOpen: boolean;
  onClose: () => void;
  onStartInterview: (interviewId: string) => void;
  onDismiss: () => void;
  interview?: RecommendedItem;
}

export const InterviewForYouModal: React.FC<InterviewForYouModalProps> = React.memo(({
  isOpen,
  onClose,
  onStartInterview,
  onDismiss,
  interview
}) => {
  // Memoize the interview data to prevent unnecessary re-renders when interview object changes reference
  const stableInterview = useMemo(() => {
    if (!interview) return null;
    
    return {
      id: interview.id,
      title: interview.title || interview.metadata?.title,
      description: interview.description || interview.metadata?.description,
      company: interview.metadata?.company,
      role: interview.metadata?.role,
      durationMinutes: interview.metadata?.durationMinutes,
    };
  }, [interview?.id, interview?.title, interview?.metadata?.title, interview?.metadata?.company, interview?.metadata?.role, interview?.metadata?.durationMinutes, interview?.description, interview?.metadata?.description]);

  // Log when the component renders and when props change
  useEffect(() => {
    if (stableInterview) {
      console.log('InterviewForYouModal: Component rendered with stable interview:', stableInterview);
    }
  }, [stableInterview]);

  // Log when modal visibility changes
  useEffect(() => {
    console.log('InterviewForYouModal: Modal visibility changed to:', isOpen);
    
    // When modal opens, force a repaint to ensure it displays properly
    if (isOpen) {
      console.log('InterviewForYouModal: Modal opened, forcing repaint');
      document.body.classList.add('modal-open');
      setTimeout(() => {
        window.dispatchEvent(new Event('resize'));
      }, 100);
    } else {
      console.log('InterviewForYouModal: Modal closed');
      document.body.classList.remove('modal-open');
    }
    
    return () => {
      document.body.classList.remove('modal-open');
    };
  }, [isOpen])
    
  // Memoized handlers to prevent unnecessary re-renders
  const handleStartInterview = useCallback(() => {
    console.log('InterviewForYouModal: Start interview button clicked for id:', stableInterview?.id);
    onStartInterview(stableInterview?.id || '');
    onClose();
  }, [stableInterview?.id, onStartInterview, onClose]);

  const handleDismiss = useCallback(() => {
    console.log('InterviewForYouModal: Dismiss button clicked');
    onDismiss();
    onClose();
  }, [onDismiss, onClose]);

  // Early return if not open (with log)
  if (!isOpen) {
    return null;
  }
  
  console.log('InterviewForYouModal: Rendering modal content');

  return (
    <Modal 
      isOpen={isOpen} 
      onClose={onClose}
      title="Interview For You"
      maxWidth="550px"
    >
      <div className="p-6">
        {/* Interview details - Using the style from ScheduleInterviewModal */}
        <div className="mb-6 p-4 rounded-lg border border-border-light dark:border-border-dark bg-background-light/70 dark:bg-background-dark/70 focus-within:ring-2 focus-within:ring-primary-light dark:focus-within:ring-primary-dark focus-within:border-transparent transition-all">
          <h3 className="text-lg font-semibold text-text-primary-light dark:text-text-primary-dark mb-2">
            {stableInterview?.title}
          </h3>
          <div className="mb-3 text-sm text-text-secondary-light dark:text-text-secondary-dark">
            {stableInterview?.description}
          </div>
          <div className="flex flex-col gap-2">
            {stableInterview?.company && (
            <div className="flex items-center text-sm text-text-secondary-light dark:text-text-secondary-dark">
              <Building size={16} className="mr-2 text-primary-light dark:text-primary-dark" />
              {stableInterview.company}
            </div>
            )}
            
            {stableInterview?.role && (
            <div className="flex items-center text-sm text-text-secondary-light dark:text-text-secondary-dark">
              <Briefcase size={16} className="mr-2 text-primary-light dark:text-primary-dark" />
              {stableInterview.role}
            </div>
            )}

            {/* Duration display hidden as it can be confusing for users
            {stableInterview?.durationMinutes && (
            <DurationDisplay 
              minutes={stableInterview.durationMinutes}
              variant="inline"
              clockSize={16}
              className="text-sm text-text-secondary-light dark:text-text-secondary-dark"
            />
            )} */}
          
          </div>
        
        </div>
        
        <div className="bg-primary-light/5 dark:bg-primary-dark/5 border border-primary-light/20 dark:border-primary-dark/20 p-4 rounded-lg mb-6">
          <div className="flex items-start">
            <Sparkles size={18} className="text-primary-light dark:text-primary-dark mt-0.5 mr-3 flex-shrink-0" />
            <div>
              <p className="text-base font-heading font-semibold text-text-primary-light dark:text-text-primary-dark mb-1">
                Why this interview?
              </p>
              <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark">
                We've specially curated this interview for you based on your profile and career preferences. It's designed to help you practice and prepare for real-world interviews in your field.
              </p>
            </div>
          </div>
        </div>

        <div className="mt-6 flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
          <Button 
            variant="outline" 
            onClick={handleDismiss}
            className="w-full sm:w-auto"
          >
            I'll Do It Later
          </Button>
          
          <Button 
            variant="primary" 
            leftIcon={<Play className="w-4 h-4" />}
            onClick={handleStartInterview}
            className="w-full sm:w-auto"
          >
            Start Interview
          </Button>
        </div>
      </div>
    </Modal>
  );
});

export default InterviewForYouModal; 